import { useSaveAnalysis } from '@modules/analysis/hooks/useSaveAnalysis';
import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useFeedStore } from '@modules/feed/store/feedStore';

import {
  useGetArtefactScope,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { DashboardNameAndEmojiInput } from '@modules/dashboard/components/DashboardNameAndEmojiInput';
import { DASHBOARD_NAME_MAX_LENGTH } from '@modules/dashboard/constants';
import { getEmojiFromDashboard } from '@modules/dashboard/helpers';
import {
  DashboardHeader,
  EditableDashboardListItem,
} from '@modules/dashboard/types';
import { useSearchAnalysesWhereClause } from '@modules/feed/hooks';
import { getSearchMetadataUrl } from '@modules/feed/utils';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AnalysisUseCase, Scope as EScope } from 'generated/graphql';
import { ChangeEvent, FormEvent, useState } from 'react';
import { Scope } from 'v2/core/entities/scope';
import { EQLProps, ExpressionType } from 'v2/core/repository/eql/types';
import { useGetUser } from 'v2/core/services/user';
import { FeedDefinitionInfo } from '../FeedDefinitionInfo';

interface SaveFeedAndAddToDashboardFormProps {
  onFeedCreate?: () => void;
  dashboardToAddTo?: EditableDashboardListItem;
  setSavedAnalysisRoute?: React.Dispatch<React.SetStateAction<string | null>>;
  shouldAddToNewDashboard?: boolean;
  onClose: () => void;
}

export const SaveFeedAndAddToDashboardForm = ({
  onFeedCreate,
  dashboardToAddTo,
  setSavedAnalysisRoute,
  shouldAddToNewDashboard,
  onClose,
}: SaveFeedAndAddToDashboardFormProps) => {
  const [feedName, setFeedName] = useState('');
  const [newDashboardName, setNewDashboardName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dialogRefInState, setDialogRefInState] =
    useState<HTMLDivElement | null>(null);
  const [emoji, setEmoji] = useState(getEmojiFromDashboard());

  const searchQuery = useFeedStore(({ query }) => query);
  const searchWhereClause = useSearchAnalysesWhereClause();
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();
  const eqlProps: Omit<EQLProps, 'sourceDetailsMap'> = {
    expressionType: ExpressionType.Records,
    whereClause: searchWhereClause,
    dateRange: searchQuery.duration as EQLProps['dateRange'],
  };

  const metadata = getSearchMetadataUrl({
    tenantSlug,
    ...searchQuery!,
    isSearchToFeedEnabled,
  });

  const { onSaveAnalysis } = useSaveAnalysis({
    eqlProps,
    metadata: metadata,
    onAnalysisCreate: onFeedCreate,
    dashboardToAddTo,
    shouldRedirect: false,
    useCase: AnalysisUseCase.Feed,
  });

  const artefactScope = useGetArtefactScope();

  const { closeModal } = useMuiModalContext();

  const isSaveButtonDisabled =
    !feedName.trim() ||
    (shouldAddToNewDashboard &&
      (!newDashboardName?.trim() ||
        newDashboardName.length > DASHBOARD_NAME_MAX_LENGTH));

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
      return;
    }
    closeModal();
  };

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFeedName(value);
  };

  const onSaveFeed = async (
    name: string,
    scope: Scope,
    newDashboard?: DashboardHeader,
  ) => {
    const artefactDetails = await onSaveAnalysis(name, scope, newDashboard);
    setSavedAnalysisRoute?.(artefactDetails.artefactRoute);
    return artefactDetails;
  };

  const onSaveFeedAnalysis = async (e: FormEvent) => {
    e.preventDefault();

    const analysisScope =
      artefactScope === EScope.Private ? Scope.PRIVATE : Scope.PUBLIC;

    if (isSaveButtonDisabled) {
      return;
    }

    setIsLoading(true);
    await onSaveFeed(feedName, analysisScope, {
      name: newDashboardName ?? '',
      emoji: emoji,
    });
    handleCloseModal();
  };

  const renderSaveButton = () => {
    return (
      <LoadingButton
        variant="contained"
        type="submit"
        disabled={isSaveButtonDisabled}
        loading={isLoading}
        loadingPosition="start"
        endIcon={<ReturnHotKeyIcon isDisabled={isSaveButtonDisabled} />}
      >
        Save Feed
        {dashboardToAddTo || shouldAddToNewDashboard ? ' & Add' : ''}
      </LoadingButton>
    );
  };

  return (
    <>
      <ModalHeader title="Saving Feed" showCloseButton={false} />
      <Box
        component="form"
        onSubmit={onSaveFeedAnalysis}
        ref={setDialogRefInState}
      >
        <DialogContent dividers>
          <Stack py={2} gap={4}>
            {dashboardToAddTo && (
              <Typography variant="subtitle2" color="text.secondary">
                Save this Feed to add to "{dashboardToAddTo.title}" Dashboard
              </Typography>
            )}
            {shouldAddToNewDashboard && (
              <DashboardNameAndEmojiInput
                emoji={emoji}
                onEmojiChange={setEmoji}
                name={newDashboardName}
                onNameChange={setNewDashboardName}
                dialogRef={dialogRefInState}
              />
            )}
            <TextField
              autoFocus={!shouldAddToNewDashboard}
              label="Feed Name"
              type={'text'}
              placeholder={'Enter descriptive name'}
              name={`feed-name`}
              value={feedName}
              onChange={onNameChange}
              autoComplete="off"
            />

            <Box
              sx={{
                borderRadius: 1,
                backgroundColor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box p={2} color="text.secondary">
                <FeedDefinitionInfo />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" m={2} spacing={3}>
            <Button
              variant="outlined"
              onClick={handleCloseModal}
              type="button"
              disabled={isLoading}
              endIcon={
                <EscHotKeyIcon
                  isDisabled={
                    isLoading ||
                    Boolean(dashboardToAddTo) ||
                    shouldAddToNewDashboard
                  }
                />
              }
            >
              Cancel
            </Button>
            {isSaveButtonDisabled ? (
              <Tooltip
                title={`Please add a name to save the ${
                  shouldAddToNewDashboard && !newDashboardName?.trim()
                    ? 'Dashboard'
                    : 'Feed'
                }`}
                placement="top"
                arrow
              >
                <Box>{renderSaveButton()}</Box>
              </Tooltip>
            ) : (
              renderSaveButton()
            )}
          </Stack>
        </DialogActions>
      </Box>
    </>
  );
};
